/**
 * Centralized Feature Flags Configuration
 * 
 * Single source of truth for all feature flags.
 * Consolidates scattered flags for easy management and GCP migration.
 * 
 * NOTE: This is the ONLY feature flags file. Do not create additional flag files.
 */

import { structuredLogger } from "@/lib/logging";

// Feature flag sources
type FlagSource = "env" | "database" | "static";

interface FeatureFlagDefinition {
  key: string;
  defaultValue: boolean;
  source: FlagSource;
  description: string;
}

// Central registry of all feature flags
const FLAG_DEFINITIONS: Record<string, FeatureFlagDefinition> = {
  // ==================== INTERNAL TOOLS ====================
  ENABLE_INTERNAL_TOOLS: {
    key: "VITE_ENABLE_INTERNAL_TOOLS",
    defaultValue: false,
    source: "env",
    description: "Enable internal development tools (backlog, impersonation, etc.)",
  },
  
  // ==================== PMF FEATURE MODE FLAGS ====================
  ENABLE_CORE_MODE: {
    key: "ENABLE_CORE_MODE",
    defaultValue: true,
    source: "static",
    description: "Core Mode: Emphasizes weekly planning, progress, and parent communication",
  },
  ENABLE_ADVANCED_TRAINING_FEATURES: {
    key: "ENABLE_ADVANCED_TRAINING_FEATURES",
    defaultValue: true,
    source: "static",
    description: "Advanced Training Features: Reveals Collegiate Pathway, Activity Feed, and Recruitment",
  },
  
  // ==================== AI DEVELOPMENT ASSISTANT FLAGS ====================
  ENABLE_AI_ASSISTANT_UI: {
    key: "ENABLE_AI_ASSISTANT_UI",
    defaultValue: true,
    source: "static",
    description: "AI Assistant UI - Main AI hub and persona pages",
  },
  ENABLE_AI_COACH_ASSISTANT: {
    key: "ENABLE_AI_COACH_ASSISTANT",
    defaultValue: true,
    source: "static",
    description: "MVP AI Features - Coach assistant enabled",
  },
  ENABLE_AI_PARENT_HELPER: {
    key: "ENABLE_AI_PARENT_HELPER",
    defaultValue: true,
    source: "static",
    description: "MVP AI Features - Parent helper enabled",
  },
  ENABLE_AI_PLAYER_TUTOR: {
    key: "ENABLE_AI_PLAYER_TUTOR",
    defaultValue: true,
    source: "static",
    description: "MVP AI Features - Player tutor enabled",
  },
  ENABLE_AI_ORG_INGESTION: {
    key: "ENABLE_AI_ORG_INGESTION",
    defaultValue: true,
    source: "static",
    description: "Enable organization knowledge base ingestion (MVP Plus)",
  },
  ENABLE_AI_PLAYER_OPEN_CHAT: {
    key: "ENABLE_AI_PLAYER_OPEN_CHAT",
    defaultValue: false,
    source: "static",
    description: "MVP+ AI - Player open chat (ai_ip_safety_mvp_plus = OFF)",
  },
  ENABLE_AI_EXPERIMENTAL: {
    key: "ENABLE_AI_EXPERIMENTAL",
    defaultValue: false,
    source: "static",
    description: "MVP+ AI - Experimental features (ai_player_advanced_mvp_plus = OFF)",
  },
  ENABLE_AI_PREMIUM: {
    key: "ENABLE_AI_PREMIUM",
    defaultValue: false,
    source: "static",
    description: "AI Premium features",
  },
  
  // ==================== AI ENHANCEMENTS ====================
  ENABLE_AI_PATHWAY_CONTEXT: {
    key: "ENABLE_AI_PATHWAY_CONTEXT",
    defaultValue: true,
    source: "static",
    description: "MVP AI Enhancements - Pathway context and agentic routing",
  },
  ENABLE_AI_MICRO_FEEDBACK: {
    key: "ENABLE_AI_MICRO_FEEDBACK",
    defaultValue: true,
    source: "static",
    description: "MVP AI Enhancements - Micro feedback enabled",
  },
  ENABLE_AI_PLAYER_REFLECTION: {
    key: "ENABLE_AI_PLAYER_REFLECTION",
    defaultValue: true,
    source: "static",
    description: "MVP AI Enhancements - Player reflection enabled",
  },
  ENABLE_AI_PRACTICE_SKELETON: {
    key: "ENABLE_AI_PRACTICE_SKELETON",
    defaultValue: true,
    source: "static",
    description: "MVP AI Enhancements - Practice skeleton enabled",
  },
  ENABLE_AI_NOTES_TO_PARENT: {
    key: "ENABLE_AI_NOTES_TO_PARENT",
    defaultValue: true,
    source: "static",
    description: "MVP AI Enhancements - Notes to parent enabled",
  },
  
  // ==================== COACH HELPERS ====================
  ENABLE_COACH_SMART_HELPERS: {
    key: "ENABLE_COACH_SMART_HELPERS",
    defaultValue: true,
    source: "static",
    description: "Coach Smart Helpers - Lightweight writing/suggestion tools for coaches ONLY",
  },
  ENABLE_SMART_COACH_PROMPTS: {
    key: "ENABLE_SMART_COACH_PROMPTS",
    defaultValue: true,
    source: "static",
    description: "Smart Coach Prompts - Contextual AI suggestions during player evaluations",
  },
  
  // ==================== GG FOX MASCOT FLAGS ====================
  ENABLE_GG_FOX_COACH: {
    key: "ENABLE_GG_FOX_COACH",
    defaultValue: true,
    source: "static",
    description: "GG Fox mascot visibility for coaches - appears ONLY in AI contexts",
  },
  ENABLE_GG_FOX_ADMIN: {
    key: "ENABLE_GG_FOX_ADMIN",
    defaultValue: true,
    source: "static",
    description: "GG Fox mascot visibility for admins",
  },
  ENABLE_GG_FOX_PARENT: {
    key: "ENABLE_GG_FOX_PARENT",
    defaultValue: false,
    source: "static",
    description: "GG Fox mascot visibility for parents (disabled for MVP)",
  },
  ENABLE_GG_FOX_PLAYER: {
    key: "ENABLE_GG_FOX_PLAYER",
    defaultValue: false,
    source: "static",
    description: "GG Fox mascot visibility for players (COPPA-gated, disabled for MVP)",
  },
  
  
  // ==================== BILLING ====================
  ENABLE_BILLING: {
    key: "ENABLE_BILLING",
    defaultValue: false,
    source: "static",
    description: "Enable subscription billing surfaces",
  },
  ENABLE_BILLING_UI: {
    key: "ENABLE_BILLING_UI",
    defaultValue: true,
    source: "static",
    description: "Billing UI - Shows plans and upgrade prompts",
  },
  
  // ==================== FRACTAL KX ECOSYSTEM INTEGRATION ====================
  // @deprecated-review Q3 2025 - External ecosystem features, pending partner integration
  ENABLE_MCP_EXTERNAL_SOURCES: {
    key: "ENABLE_MCP_EXTERNAL_SOURCES",
    defaultValue: false,
    source: "static",
    description: "Project Lattice MCP Integration - Read-only consumption of external MCP outputs",
  },
  // @deprecated-review Q3 2025 - Depends on MCP integration
  ENABLE_ADVANCED_PERFORMANCE_INSIGHTS: {
    key: "ENABLE_ADVANCED_PERFORMANCE_INSIGHTS",
    defaultValue: false,
    source: "static",
    description: "Advanced Performance Insights (Beta) - Parent-safe summaries from Project Lattice",
  },
  // @deprecated-review Q3 2025 - Cross-platform identity, pending ecosystem alignment
  ENABLE_FRACTAL_IDENTITY: {
    key: "ENABLE_FRACTAL_IDENTITY",
    defaultValue: false,
    source: "static",
    description: "Fractal Person ID - Shared identity abstraction for ecosystem alignment",
  },
  
  // ==================== TERMINOLOGY & INTENT AUDIT ====================
  ENABLE_TERMINOLOGY_AUDIT: {
    key: "ENABLE_TERMINOLOGY_AUDIT",
    defaultValue: true,
    source: "static",
    description: "Terminology Audit Scanner - Detects disallowed terminology and intent violations",
  },
  
  // ==================== PHASE 2 FEATURES ====================
  // @deprecated-review Q2 2025 - Roadmap Phase 2, currently app-admin gated
  ENABLE_PHASE2_FEATURES: {
    key: "ENABLE_PHASE2_FEATURES",
    defaultValue: false,
    source: "static",
    description: "Phase 2 features - Gated at route level for app admin access only",
  },

  // ==================== INSTITUTIONS / ORGANIZATIONS ====================
  ENABLE_ORG_FEATURES: {
    key: "ENABLE_ORG_FEATURES",
    defaultValue: false,
    source: "static",
    description: "Organization/institution surfaces (org switcher, org admin tools). Off until institutions are onboarded.",
  },


  
  // ==================== DEBUG FEATURES ====================
  ENABLE_DEBUG_MODE: {
    key: "VITE_DEBUG_MODE",
    defaultValue: false,
    source: "env",
    description: "Enable debug mode with extra logging",
  },

  // ==================== AGE BACKFILL ====================
  AGE_BACKFILL_PROMPT: {
    key: "VITE_AGE_BACKFILL_PROMPT",
    defaultValue: false,
    source: "env",
    description:
      "Prompt existing accounts with no user_age_verification row on next login. Default OFF. Do not enable without a reviewed dry-run.",
  },
} as const;

// Type-safe flag names
export type FeatureFlagName = keyof typeof FLAG_DEFINITIONS;

/**
 * Get a feature flag value
 */
export function getFeatureFlag(flagName: FeatureFlagName): boolean {
  const definition = FLAG_DEFINITIONS[flagName];
  
  if (!definition) {
    structuredLogger.warn("Unknown feature flag requested", { flagName });
    return false;
  }
  
  switch (definition.source) {
    case "env":
      return import.meta.env[definition.key] === "true";
    case "database":
      // Database flags would be fetched async - return default for sync access
      return definition.defaultValue;
    case "static":
    default:
      return definition.defaultValue;
  }
}

/**
 * Get all feature flags and their current values
 */
export function getAllFeatureFlags(): Record<FeatureFlagName, boolean> {
  const flags: Partial<Record<FeatureFlagName, boolean>> = {};
  
  for (const flagName of Object.keys(FLAG_DEFINITIONS) as FeatureFlagName[]) {
    flags[flagName] = getFeatureFlag(flagName);
  }
  
  return flags as Record<FeatureFlagName, boolean>;
}

/**
 * Get feature flag metadata for admin UI
 */
export function getFeatureFlagMetadata(): Record<FeatureFlagName, FeatureFlagDefinition> {
  return { ...FLAG_DEFINITIONS };
}

/**
 * Check if a feature is enabled (alias for getFeatureFlag)
 */
export function isFeatureEnabled(flagName: FeatureFlagName): boolean {
  return getFeatureFlag(flagName);
}

// ==================== EXPORTED FLAG CONSTANTS ====================
// These are exported for direct import compatibility with existing code

// Internal Tools
export const INTERNAL_TOOLS_ENABLED = getFeatureFlag("ENABLE_INTERNAL_TOOLS");

// PMF Feature Mode
export const ENABLE_CORE_MODE = getFeatureFlag("ENABLE_CORE_MODE");
export const ENABLE_ADVANCED_TRAINING_FEATURES = getFeatureFlag("ENABLE_ADVANCED_TRAINING_FEATURES");

// AI Development Assistant
export const ENABLE_AI_ASSISTANT_UI = getFeatureFlag("ENABLE_AI_ASSISTANT_UI");
export const ENABLE_AI_COACH_ASSISTANT = getFeatureFlag("ENABLE_AI_COACH_ASSISTANT");
export const ENABLE_AI_PARENT_HELPER = getFeatureFlag("ENABLE_AI_PARENT_HELPER");
export const ENABLE_AI_PLAYER_TUTOR = getFeatureFlag("ENABLE_AI_PLAYER_TUTOR");
export const ENABLE_AI_ORG_INGESTION = getFeatureFlag("ENABLE_AI_ORG_INGESTION");
export const ENABLE_AI_PLAYER_OPEN_CHAT = getFeatureFlag("ENABLE_AI_PLAYER_OPEN_CHAT");
export const ENABLE_AI_EXPERIMENTAL = getFeatureFlag("ENABLE_AI_EXPERIMENTAL");
export const ENABLE_AI_PREMIUM = getFeatureFlag("ENABLE_AI_PREMIUM");

// AI Enhancements
export const ENABLE_AI_PATHWAY_CONTEXT = getFeatureFlag("ENABLE_AI_PATHWAY_CONTEXT");
export const ENABLE_AI_MICRO_FEEDBACK = getFeatureFlag("ENABLE_AI_MICRO_FEEDBACK");
export const ENABLE_AI_PLAYER_REFLECTION = getFeatureFlag("ENABLE_AI_PLAYER_REFLECTION");
export const ENABLE_AI_PRACTICE_SKELETON = getFeatureFlag("ENABLE_AI_PRACTICE_SKELETON");
export const ENABLE_AI_NOTES_TO_PARENT = getFeatureFlag("ENABLE_AI_NOTES_TO_PARENT");

// Coach Helpers
export const ENABLE_COACH_SMART_HELPERS = getFeatureFlag("ENABLE_COACH_SMART_HELPERS");
export const ENABLE_SMART_COACH_PROMPTS = getFeatureFlag("ENABLE_SMART_COACH_PROMPTS");

// GG Fox Mascot
export const ENABLE_GG_FOX_COACH = getFeatureFlag("ENABLE_GG_FOX_COACH");
export const ENABLE_GG_FOX_ADMIN = getFeatureFlag("ENABLE_GG_FOX_ADMIN");
export const ENABLE_GG_FOX_PARENT = getFeatureFlag("ENABLE_GG_FOX_PARENT");
export const ENABLE_GG_FOX_PLAYER = getFeatureFlag("ENABLE_GG_FOX_PLAYER");


// Billing
export const BILLING_ENABLED = getFeatureFlag("ENABLE_BILLING");
export const ENABLE_BILLING_UI = getFeatureFlag("ENABLE_BILLING_UI");

// Fractal KX Ecosystem
export const ENABLE_MCP_EXTERNAL_SOURCES = getFeatureFlag("ENABLE_MCP_EXTERNAL_SOURCES");
export const ENABLE_ADVANCED_PERFORMANCE_INSIGHTS = getFeatureFlag("ENABLE_ADVANCED_PERFORMANCE_INSIGHTS");
export const ENABLE_FRACTAL_IDENTITY = getFeatureFlag("ENABLE_FRACTAL_IDENTITY");

// Terminology & Audit
export const ENABLE_TERMINOLOGY_AUDIT = getFeatureFlag("ENABLE_TERMINOLOGY_AUDIT");

// Phase 2
export const ENABLE_PHASE2_FEATURES = getFeatureFlag("ENABLE_PHASE2_FEATURES");

// Organizations / Institutions
export const ENABLE_ORG_FEATURES = getFeatureFlag("ENABLE_ORG_FEATURES");

// Debug
export const DEBUG_MODE_ENABLED = getFeatureFlag("ENABLE_DEBUG_MODE");

// Age backfill (existing accounts). Default off unless VITE_AGE_BACKFILL_PROMPT=true.
export const AGE_BACKFILL_PROMPT = getFeatureFlag("AGE_BACKFILL_PROMPT");
